import "../styles/UserMenuAccount.css";
import { useTranslation } from "react-i18next";
import ChatTitle from "./ChatTitle";
import { FiChevronRight } from "react-icons/fi";
import { normalizeStatus, getStatusLabel } from "../utils/orderStatus";

export default function UserMenuAccount({
  displayName,
  email,
  points = null,
  purchases = [],
  chats = [],
  onLogout,
  onOpenOrders,
  onOpenLastOrder,
  onOpenChat,
}) {
  const { t, i18n } = useTranslation("account");
  const lastOrder = purchases?.length ? purchases[0] : null;

  function formatMoney(value) {
    const n = Number(value || 0);
    const locale = i18n.language?.startsWith("uk") ? "uk-UA" : "en-US";

    return `${new Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(n)} ${t("currency_symbol")}`;
  }

  function formatChatDate(date) {
    if (!date) return "";

    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return "";

    const locale = i18n.language?.startsWith("uk") ? "uk-UA" : "en-US";

    return new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  }

  return (
    <div className="umAcc">
      <div className="umAccTop">
        <div className="umAccUserRow">
          <div className="umAccUser">
            <div className="umAccName">{displayName}</div>
            <div className="umAccEmail">{email}</div>
          </div>

          <div className="umAccRight">
            {typeof points === "number" && (
              <div className="umAccBadge" title={t("points")}>
                <span className="umAccBadgeDot" />
                <span className="umAccBadgeNum">{points}</span>
              </div>
            )}
          </div>
        </div>

        <div className="umAccDivider" />
      </div>

      <button className="umAccSectionRow" type="button" onClick={onOpenOrders}>
        <div className="umAccSectionTitle">{t("purchase_history")}</div>

        <div className="umAccChevron" aria-hidden="true">
          <FiChevronRight className="umAccChevronIcon" />
        </div>
      </button>

      <div className="umAccDivider" />

      {!lastOrder ? (
        <div className="umAccEmpty">{t("orders_empty")}</div>
      ) : (
        <div
          key={lastOrder.id}
          className="umOrderCard"
          onClick={() => onOpenLastOrder?.(lastOrder.id)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onOpenLastOrder?.(lastOrder.id);
            }
          }}
        >
          <div className="umOrderTop">
            <div className={`umOrderStatus st-${normalizeStatus(lastOrder.status)}`}>
              {getStatusLabel(lastOrder.status)}
            </div>

            <div className="umOrderTotal">
              {typeof lastOrder.total === "number"
                ? formatMoney(lastOrder.total)
                : lastOrder.total}
            </div>
          </div>

          <div className="umOrderMeta">
            {t("order_number")}
            {lastOrder.receipt_number} • {lastOrder.date || ""}
          </div>

          {!!lastOrder.items?.length && (
            <div className="umOrderImages">
              {lastOrder.items.slice(0, 6).map((it, idx) => (
                <div
                  key={it.id ?? `${lastOrder.id}-${idx}`}
                  className="umOrderImgWrap"
                >
                  <img
                    className="umOrderImg"
                    src={it.image_url}
                    alt={it.title || t("fallback_item")}
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="umAccDivider" />

      <div className="umAccChatsTitle">{t("chat_history")}</div>

      {chats.length === 0 ? (
        <div className="umAccEmpty">{t("chat_empty")}</div>
      ) : (
        <div className="umAccChatList">
          {chats.map((c) => (
            <button
              key={c.id}
              type="button"
              className="umAccChatItem"
              onClick={() => onOpenChat?.(c.id)}
              title={c.title || ""}
            >
              <div className="umAccChatContent">
                <ChatTitle chat={c} />
                <div className="umAccChatDate">
                  {formatChatDate(c.updated_at || c.created_at)}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      <div className="umAccBottom">
        <button className="umAccLogout" onClick={onLogout}>
          {t("logout")}
        </button>
      </div>
    </div>
  );
}